import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtMoney } from "@/lib/api";
import { useSettings } from "@/hooks/useData";
import { useAuth } from "@/lib/auth";

export const useOverdue = () => useQuery({ queryKey: ["overdue"], queryFn: async () => (await api.get("/reminders/overdue")).data });

export function OverdueBanner() {
  const { data } = useOverdue();
  const { data: settings } = useSettings();
  if (!data?.count) return null;
  return (
    <Link to="/documents?type=invoice" data-testid="overdue-banner" className="flex flex-wrap items-center gap-4 border border-primary bg-primary/5 p-5 hover:bg-primary/10 transition-colors">
      <BellRing className="w-5 h-5 text-primary" strokeWidth={1.5} />
      <div className="flex-1 text-sm"><span className="font-semibold">{data.count} overdue invoice{data.count !== 1 ? "s" : ""}</span> <span className="text-muted-foreground">past their due date and still unpaid</span></div>
      <div className="font-mono font-semibold" data-testid="overdue-total">{fmtMoney(data.total, settings)}</div>
    </Link>
  );
}

export function RemindersSection({ s, set, isAdmin }) {
  const { user } = useAuth();
  const { data } = useOverdue();
  const [busy, setBusy] = useState(false);

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/reminders/overdue/send-now");
      toast.success(`Summary of ${r.documents} invoice${r.documents !== 1 ? "s" : ""} sent to ${r.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send summary");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-6 sm:p-8 space-y-5" data-testid="reminders-section">
      <h2 className="font-display text-xl font-bold">Overdue reminders</h2>
      <p className="text-sm text-muted-foreground">Every morning at 8:00 (Kuala Lumpur time) DocFlow emails a summary of invoices that went past their due date and are still unpaid. Admins get every invoice; members get their own. Each invoice is included once.</p>
      <label className="flex items-center justify-between gap-4 border border-border p-4 cursor-pointer">
        <span className="text-sm font-semibold">Daily overdue summary email</span>
        <button type="button" role="switch" aria-checked={s.overdue_reminders_enabled} disabled={!isAdmin} data-testid="settings-reminders-toggle" onClick={() => set("overdue_reminders_enabled", !s.overdue_reminders_enabled)} className={`relative w-12 h-6 border transition-colors disabled:opacity-50 ${s.overdue_reminders_enabled ? "bg-primary border-primary" : "bg-muted border-border"}`}>
          <span className={`absolute block top-0.5 w-5 h-5 bg-white transition-transform ${s.overdue_reminders_enabled ? "translate-x-6" : "translate-x-0.5"}`} />
        </button>
      </label>
      <div className="border border-border">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="text-sm"><span className="font-semibold" data-testid="overdue-count">{data?.count ?? 0}</span> <span className="text-muted-foreground">currently overdue</span></div>
          <button type="button" onClick={sendNow} disabled={busy || !data?.count} data-testid="reminders-send-now" className="btn-press h-9 px-3 text-xs font-semibold border border-foreground hover:bg-foreground hover:text-background flex items-center gap-2 disabled:opacity-40">
            <Send className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Sending…" : `Email me now (${user?.email})`}
          </button>
        </div>
        {data?.documents?.slice(0, 5).map((d) => (
          <Link key={d.id} to={`/documents/${d.id}`} className="flex items-center gap-4 px-4 py-3 text-sm border-b border-border/40 last:border-0 hover:bg-muted transition-colors">
            <span className="font-mono">{d.number}</span>
            <span className="flex-1 truncate text-muted-foreground">{d.party?.name}</span>
            <span className="text-xs text-muted-foreground">due {fmtDate(d.due_date)}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
